import {
  BusinessUnitOption,
  OrganizationService,
} from '@/_shared/services/organization.service';
import container from '@/_shared/services/service-container';
import { ChevronDown } from 'lucide-react';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';

export const BusinessUnitSwitcher: React.FC = () => {
  const organizationService = container.get<OrganizationService>(
    'OrganizationService'
  );
  const { data: session, update } = useSession();
  const router = useRouter();

  const [businessUnits, setBusinessUnits] = useState<BusinessUnitOption[]>([]);
  const [dropdownVisible, setDropdownVisible] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const activeBusinessUnitId = session?.user?.businessUnitId;

  const loadBusinessUnits = useCallback(() => {
    organizationService.getBusinessUnits().then((response) => {
      if (response.data) {
        setBusinessUnits(response.data);
      }
    });
  }, [organizationService]);

  useEffect(() => {
    if (router.isReady) {
      loadBusinessUnits();
    }
  }, [loadBusinessUnits, router.isReady]);

  const handleClickOutside = (event: MouseEvent): void => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      buttonRef.current &&
      !buttonRef.current.contains(event.target as Node)
    ) {
      setDropdownVisible(false);
      document.removeEventListener('mousedown', handleClickOutside);
    }
  };

  const toggleDropdown = (): void => {
    if (dropdownVisible) {
      document.removeEventListener('mousedown', handleClickOutside);
    } else {
      document.addEventListener('mousedown', handleClickOutside);
    }
    setDropdownVisible(!dropdownVisible);
  };

  const handleSelect = async (
    businessUnit: BusinessUnitOption
  ): Promise<void> => {
    setDropdownVisible(false);
    if (businessUnit.id === activeBusinessUnitId) {
      return;
    }
    await update({
      trigger: 'switch-business-unit',
      businessUnitId: businessUnit.id,
    });
    router.reload();
  };

  if (businessUnits.length <= 1) {
    return null;
  }

  const activeBusinessUnit = businessUnits.find(
    (bu) => bu.id === activeBusinessUnitId
  );

  return (
    <div className="relative">
      <button
        type="button"
        ref={buttonRef}
        onClick={toggleDropdown}
        className="flex items-center gap-1 text-sm font-medium text-gray-800 hover:bg-gray-100 rounded-lg px-2 py-1"
      >
        <span>{activeBusinessUnit?.name ?? 'Select business unit'}</span>
        <ChevronDown className="w-4" />
      </button>
      {dropdownVisible && (
        <div
          ref={dropdownRef}
          className="absolute right-0 mt-2 w-48 bg-white border border-gray-border rounded-xl shadow-lg z-50 text-sm font-medium transition-transform transform origin-top-right opacity-0 animate-dropdown"
        >
          <ul>
            {businessUnits.map((businessUnit, index) => (
              <li key={businessUnit.id}>
                <button
                  type="button"
                  className={`flex items-center gap-2 w-full text-left px-4 py-2 text-gray-800 hover:bg-indigo-100 ${
                    index === 0 ? 'rounded-t-lg' : ''
                  } ${index === businessUnits.length - 1 ? 'rounded-b-lg' : ''}`}
                  onClick={() => handleSelect(businessUnit)}
                >
                  <span>{businessUnit.name}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default BusinessUnitSwitcher;
